import { providerConfigurationApi } from '@/api/configuration/providerConfigurationApi';
import { type Provider, type Integration, type PaymentIntegrationConfig, PaymentMethodsAccepted } from '@/models/configuration/ProviderConfiguration';
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext, useWatch, useFieldArray } from "react-hook-form";
import { useCurrentBrand } from "../../hooks/useCurrentBrand";
import JsonEditor from "../common/JsonEditor";
import Spacer from "../common/Spacer";
import Box from "@mui/material/Box";
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TabWithError from '../common/TabWithError';
import FormTextField from '../common/FormTextField';
import BitFlagSelect from '../common/BitFlagSelect';
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridRowParams } from "@mui/x-data-grid";
import DataGridEditButton from "../common/data-grid/DataGridEditButton";
import DataGridDeleteButton from "../common/data-grid/DataGridDeleteButton";
import { PosFieldNameMapType } from "@/models/enums/PosFieldNameMapType";
import { type PosFieldNameMap } from "@/models/configuration/PosProviderConfiguration";
import { prettifyEnumName } from "@/models/enums/EnumHelper";

// A provider section copied wholesale from another location (Copy Configuration). Records the vendor/version
// the copied JSON belongs to so the vendor-change effects below can keep it instead of re-fetching a blank
// template (which would silently drop the copied configurationJson).
export type CopiedConfig = { vendor: string; version: string; configurationJson: string; fieldNameMaps?: unknown[]; integrationConfigurations?: PaymentIntegrationConfig[] };

// Well-known integration key for a vendor's shared-credentials pseudo-integration. Rendered as a top section
// above the per-integration accordions (see sharedIndex in ProviderConfig).
const SHARED_INTEGRATION_KEY = 'shared';

interface ProviderConfigProps {
    kind: number;
    isEditing: boolean;
    copiedConfigRef?: React.MutableRefObject<Record<string, CopiedConfig>>;
    // Bumped by LocationDetails each time a Copy Configuration completes, so this section re-applies the
    // just-copied config authoritatively — needed for fieldNameMaps (useFieldArray doesn't pick up a parent
    // setValue) and for a same-vendor/version copy (which wouldn't trigger the vendor-change effect).
    copyNonce?: number;
}

const ProviderConfig = ({ kind, isEditing, copiedConfigRef, copyNonce }: ProviderConfigProps) => {
    const { control, register, setValue, getValues, trigger, getFieldState, formState } = useFormContext();
    const configFieldByKind: Record<number, string> = {
        0: "posConfiguration",
        1: "paymentConfiguration",
        2: "pmsConfiguration",
        3: "loyaltyConfiguration",
        4: "feedbackConfiguration",
        5: "imagesConfiguration",
        6: "nutritionConfiguration",
    };
    const [tabValue, setTabValue] = useState<string>('vendor');
    const basePath = configFieldByKind[kind];
    // Brand-level gate: the feedback location "General" tab only shows when the brand has TouchTab Feedback enabled.
    const { data: currentBrand } = useCurrentBrand();
    const brandFeedbackEnabled = currentBrand?.touchTabFeedbackEnabled ?? false;
    // The loyalty vendor is chosen once at the brand level and locked at the location — a location can't run a
    // different loyalty program than its brand. So for the loyalty tab the vendor is read-only and forced to
    // the brand's selection (below).
    const brandLoyaltyVendor = currentBrand?.loyaltyConfiguration?.vendor ?? '';
    const loyaltyVendorLocked = kind === 3;
    const vendor = useWatch({ name: `${basePath}.vendor` }); // Get reference to current vendor
    const version = useWatch({ name: `${basePath}.version` }); // Get reference to current version
    const loyaltyVendor = useWatch({ name: `loyaltyConfiguration.vendor` }); // POS: the loyalty item id only applies when the location has a loyalty vendor
    const sendServiceChargeToPos = useWatch({ name: `${basePath}.sendServiceChargeToPos` }); // POS: gates the Service Charge ID field
    const useTouchTabTenderType = useWatch({ name: `${basePath}.tenderConfiguration.useTouchTabTenderId` }); // POS: toggles which tender ids apply
    // POS: the non-card tender ids are gated by their PAYMENT METHOD, which lives on the Payment tab - an
    // absolute path, not one under basePath. Accepting the method is the decision; the tender id is only
    // the POS-side mapping for it, so there is nothing to fill in for a method the location does not take.
    const acceptedMethods = (useWatch({ name: 'paymentConfiguration.paymentMethodsAccepted' }) as number | undefined) ?? 0;
    const cashAccepted = acceptedMethods & PaymentMethodsAccepted.Cash;
    const giftCardAccepted = acceptedMethods & PaymentMethodsAccepted.GiftCard;
    const applePayEnabled = useWatch({ name: `${basePath}.digitalWalletConfiguration.applePayEnabled` }); // Payment: gates the Apple Pay fields
    const googlePayEnabled = useWatch({ name: `${basePath}.digitalWalletConfiguration.googlePayEnabled` }); // Payment: gates the Google Pay fields

    // POS: UI-only toggle for the check-retrieval time period. It's not part of the model and isn't persisted —
    // it just gates the minutes field. Whenever it's off (or on with a blank field), checkRetrievalTimePeriodMinutes
    // is null. Seed it from the saved value, and re-seed when a different config loads (default value changes) —
    // NOT from the live value, so clearing the field while enabled doesn't flip the toggle off.
    const checkRetrievalDefault = (formState.defaultValues as any)?.[basePath]?.checkRetrievalTimePeriodMinutes;
    const [checkRetrievalEnabled, setCheckRetrievalEnabled] = useState<boolean>(checkRetrievalDefault != null);
    useEffect(() => {
        setCheckRetrievalEnabled(checkRetrievalDefault != null);
    }, [checkRetrievalDefault]);
    //const enabled = !!vendor && !!version; // wait until both are set
    //const qc = useQueryClient();
    // The provider/version list is static config and its backend fans out to vendor APIs, so cache it for
    // the session instead of refetching every time the POS/Payment tab mounts.
    const { data: providers } = useQuery<Provider[]>({ queryKey: ['providers', kind], queryFn: () => providerConfigurationApi.listProviders(kind), staleTime: Infinity, gcTime: Infinity });
    // Payment vendors can expose several integrations (card-not-present + one or more card-present). When they do,
    // the Vendor tab shows one config accordion per integration; other kinds (and non-integration-aware payment
    // vendors) keep a single config editor.
    const { data: integrations } = useQuery<Integration[]>({ queryKey: ['integrations', kind, vendor], enabled: kind === 1 && !!vendor, queryFn: () => providerConfigurationApi.listIntegrations(kind, vendor), staleTime: Infinity, gcTime: Infinity });
    // True when this is a payment vendor that exposes explicit integrations → edit per-integration via accordions.
    const useIntegrationAccordions = kind === 1 && (integrations?.length ?? 0) > 0;
    // Some vendors expose a "shared" pseudo-integration carrying credentials common to every integration (e.g.
    // FreedomPay's StoreId). It's rendered once as a top section instead of an accordion. It's still a normal
    // entry in `integrations`/`integrationConfigurations` (the reconcile effect keeps them index-aligned), so the
    // accordion map below SKIPS it by key rather than filtering the array — filtering would shift indices and
    // break the by-index binding.
    const sharedIndex = (integrations ?? []).findIndex(i => i.key === SHARED_INTEGRATION_KEY);
    // The enable/disable toggle only makes sense when a vendor exposes more than one real (non-shared)
    // integration — otherwise the location's single integration is simply always on (no toggle rendered).
    const realIntegrationCount = (integrations ?? []).filter(i => i.key !== SHARED_INTEGRATION_KEY).length;
    const showEnabledToggle = realIntegrationCount > 1;
    // Digital wallets (Apple/Google Pay) are a card-not-present feature — show the Wallets tab only when the
    // selected vendor exposes a card-not-present integration AND it's enabled for this location. The shared
    // pseudo-integration also reports the card-not-present concept on the wire, so exclude it by key.
    const integrationConfigs = useWatch({ name: `${basePath}.integrationConfigurations` }) as PaymentIntegrationConfig[] | undefined;
    const cnpIntegration = (integrations ?? []).find(i => i.key !== SHARED_INTEGRATION_KEY && i.concept === 'card-not-present');
    const cnpEnabled = !!cnpIntegration && (integrationConfigs?.find(c => c.integrationType === cnpIntegration.key)?.enabled ?? true);
    const showWallets = kind === 1 && cnpEnabled;
    // Every tab except Vendor belongs to a vendor, and none of it can be saved without one — the location
    // save drops a configuration whose vendor is empty, matching the backend (TTD-167).
    const hasVendor = !!vendor && vendor.trim() !== '';
    // If the active tab has just gone away — Wallets when CNP is disabled, or anything below Vendor when the
    // vendor is cleared — fall back rather than leaving the panel area blank.
    useEffect(() => {
        if (!showWallets && tabValue === 'wallets') setTabValue('vendor');
        if (!hasVendor && tabValue !== 'vendor') setTabValue('vendor');
    }, [showWallets, hasVendor, tabValue]);
    //const { data: configuration } = useQuery<ProviderConfiguration>({ queryKey: ['configuration', locationId, kind], enabled: load, queryFn: () => providerConfigurationApi.get(locationId!, kind), refetchOnWindowFocus: false });
    const { refetch: fetchEmptyTemplate, isFetching: isTemplateFetching } = useQuery({ queryKey: ['empty-config', kind, vendor, version], enabled: false, queryFn: () => providerConfigurationApi.getEmpty(kind, vendor!, version!), select: (s) => s || '{}' });
    //const { mutateAsync: saveConfig } = useMutation({ mutationFn: providerConfigurationApi.update, onSuccess: () => handleSaveSuccess(), onError: () => handleSaveError() });
    //const toast = useToast();
    const strings = useLocalizedStrings();
    // Provider config only ever renders inside the Admin brand/location editors, so it answers to the same
    // grant they do.
    const perms = usePermissions();
    const canUpdateConfig = perms.canUpdate(Perm.adminConfiguration);
    const canDeleteConfig = perms.canDelete(Perm.adminConfiguration);

    // POS tender type configuration: the "card" tender ids that apply when NOT using the TouchTab tender type.
    const tenderPath = `${basePath}.tenderConfiguration`;
    const cardTenderFields = [
        { key: 'visaTenderId', label: strings.visa },
        { key: 'mastercardTenderId', label: strings.masterCard },
        { key: 'amexTenderId', label: strings.amex },
        { key: 'discoverTenderId', label: strings.discover },
        { key: 'dinersTenderId', label: strings.dinersClub },
        { key: 'jcbTenderId', label: strings.jcb },
    ];
    const cardTenderPaths = cardTenderFields.map(f => `${tenderPath}.${f.key}`);
    // Require at least one card tender id, but only when the TouchTab tender type is NOT in use.
    const validateAtLeastOneCard = () =>
        !!getValues(`${tenderPath}.useTouchTabTenderId`) ||
        cardTenderPaths.some(p => !!getValues(p)) ||
        strings.atLeastOneTenderTypeRequired;

    // Payment digital wallet configuration: Apple Pay / Google Pay fields are required only when their toggle is on.
    const walletPath = `${basePath}.digitalWalletConfiguration`;

    // Which sub-tab holds a validation error (for the tab badges + auto-switch on invalid submit).
    const configErrors = (formState.errors as any)?.[basePath] ?? {};
    const groupedErrorKeys = new Set(['vendor', 'version', 'externalId', 'tenderConfiguration', 'fieldNameMaps', 'digitalWalletConfiguration', 'configurationJson', 'locationId', 'kind']);
    const vendorTabError = !!(configErrors.vendor || configErrors.version || configErrors.externalId);
    const generalTabError = Object.keys(configErrors).some(k => !groupedErrorKeys.has(k));
    const tendersTabError = !!configErrors.tenderConfiguration;
    const fieldMapsTabError = !!configErrors.fieldNameMaps;
    const walletsTabError = !!configErrors.digitalWalletConfiguration;

    // On a failed submit, jump to the first sub-tab (in tab order) that contains an error so it's visible.
    useEffect(() => {
        if (formState.submitCount === 0) return;
        const order: { tab: string; hasError: boolean }[] = [
            { tab: 'vendor', hasError: vendorTabError },
            { tab: 'general', hasError: generalTabError },
            { tab: 'tenders', hasError: tendersTabError },
            { tab: 'fieldmaps', hasError: fieldMapsTabError },
            { tab: 'wallets', hasError: walletsTabError },
        ];
        const firstErrored = order.find(o => o.hasError);
        if (firstErrored) setTabValue(firstErrored.tab);
    }, [formState.submitCount]);

    // POS field name maps: a user-defined list (none to many) managed via a grid + add/edit dialog.
    const { fields: fieldMaps, append: appendFieldMap, update: updateFieldMap, remove: removeFieldMap, replace: replaceFieldMaps } =
        useFieldArray({ control, name: `${basePath}.fieldNameMaps` });

    // Apply a just-copied config for this section. Runs on copy (nonce bump) rather than only on vendor/version
    // change, so it also covers a same-vendor/version copy. Uses replace() for the field maps because
    // useFieldArray does not observe the parent setValue the copy dialog performed.
    useEffect(() => {
        if (!copyNonce) return;
        const copied = copiedConfigRef?.current?.[basePath];
        if (!copied) return;
        setValue(`${basePath}.configurationJson`, copied.configurationJson ?? '{}', { shouldDirty: true, shouldValidate: false });
        if (copied.fieldNameMaps) replaceFieldMaps(copied.fieldNameMaps as never[]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [copyNonce]);
    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [editingMapIndex, setEditingMapIndex] = useState<number | null>(null);
    const [mapDraft, setMapDraft] = useState<PosFieldNameMap>({ type: PosFieldNameMapType.Item, externalId: '', value: '' });

    const posFieldMapTypeLabel = (t: PosFieldNameMapType) =>
        prettifyEnumName(Object.entries(PosFieldNameMapType).find(([, v]) => v === t)?.[0] ?? '');

    const fieldMapRows = fieldMaps.map((f) => {
        const m = f as unknown as (PosFieldNameMap & { id: string });
        return { id: m.id, typeLabel: posFieldMapTypeLabel(m.type), externalId: m.externalId, value: m.value };
    });

    const openAddFieldMap = () => {
        setEditingMapIndex(null);
        setMapDraft({ type: PosFieldNameMapType.Item, externalId: '', value: '' });
        setMapDialogOpen(true);
    };
    const handleEditFieldMap = (id: string) => {
        const index = fieldMaps.findIndex(f => f.id === id);
        if (index < 0) return;
        const m = fieldMaps[index] as unknown as PosFieldNameMap;
        setEditingMapIndex(index);
        setMapDraft({ type: m.type, externalId: m.externalId, value: m.value });
        setMapDialogOpen(true);
    };
    const handleDeleteFieldMap = (id: string) => {
        const index = fieldMaps.findIndex(f => f.id === id);
        if (index >= 0) removeFieldMap(index);
    };
    const saveFieldMap = () => {
        if (editingMapIndex === null) appendFieldMap(mapDraft);
        else updateFieldMap(editingMapIndex, mapDraft);
        setMapDialogOpen(false);
    };

    const fieldMapColumns: GridColDef[] = [
        { field: 'typeLabel', headerName: strings.type, flex: 1 },
        { field: 'externalId', headerName: strings.externalId, flex: 1 },
        { field: 'value', headerName: strings.value, flex: 1 },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1,
            getActions: (params: GridRowParams) => [
                <DataGridEditButton id={params.id.toString()} onClick={handleEditFieldMap} aria-label="edit field map" readOnly={!canUpdateConfig} />,
                <DataGridDeleteButton id={params.id.toString()} warningMessage={strings.fieldMapConfirmDelete} onClick={handleDeleteFieldMap} aria-label="delete field map" hidden={!canDeleteConfig} />,
            ],
        },
    ];

    const isFirstRender = useRef(true);

    useEffect(() => {
        // Integration-aware payment vendors edit per-integration below (reconcile effect), not this single JSON.
        if (useIntegrationAccordions) return;
        if (!vendor || !version) return;

        // A copy just set this section's vendor/version/config as a unit — keep the copied JSON instead of
        // clobbering it with the vendor's empty template. Idempotent, so it survives the version reshuffle
        // that selecting a new vendor triggers below.
        const copied = copiedConfigRef?.current?.[basePath];
        if (copied && copied.vendor === vendor && copied.version === version) {
            setValue(`${basePath}.configurationJson`, copied.configurationJson ?? '{}', { shouldDirty: true, shouldValidate: false });
            return;
        }
        // Moved off the copied vendor/version (a genuine user switch) — drop the stale copy marker so the
        // normal template/saved-restore logic takes over from here.
        if (copied && copiedConfigRef?.current) delete copiedConfigRef.current[basePath];

        // Switching (back) to the vendor/version that's saved on the server for this location: restore the
        // saved config JSON instead of a blank template. defaultValues tracks the last load AND save
        // (LocationDetails resets to the saved values on both), so this stays correct after a save too — unlike
        // a one-time snapshot, which would go stale the moment the user saves a freshly-picked vendor.
        const saved = (formState.defaultValues as Record<string, any> | undefined)?.[basePath];
        if (saved && saved.vendor === vendor && saved.version === version) {
            setValue(`${basePath}.configurationJson`, saved.configurationJson ?? '{}', { shouldDirty: false, shouldValidate: false });
            return;
        }

        // Otherwise fetch the empty template for the selected vendor/version.
        fetchEmptyTemplate().then(res => {
            if (!res || !res.data) return;
            setValue(`${basePath}.configurationJson`, res.data.configurationJson, { shouldDirty: false, shouldValidate: false });
        });
    }, [version, useIntegrationAccordions]);

    // Get version for selected vendor    
    const byVendor = useMemo(() => Object.fromEntries((providers ?? []).map(p => [p.vendor, p.versions])), [providers]); // Lookup map of vendor to versions
    const versions = byVendor[vendor] ?? [];

    // Per-integration version selector for an accordion (payment vendors). Each integration picks its own version
    // from the vendor's list, so a location can run e.g. AFCC 1.1 while Hosted Payment Page stays 1.0. Always shown
    // (even when a single version is offered); hidden only until the vendor's version list has loaded.
    const renderIntegrationVersion = (index: number) => (versions ?? []).length === 0 ? null : (
        <Controller
            name={`${basePath}.integrationConfigurations.${index}.version`}
            control={control}
            render={({ field }) => (
                <FormControl sx={{ width: '20ch', mb: 2 }}>
                    <InputLabel id={`integration-version-label-${index}`}>{strings.version}</InputLabel>
                    <Select
                        labelId={`integration-version-label-${index}`}
                        label={strings.version}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                    >
                        {(versions ?? []).map((v) => (
                            <MenuItem key={v} value={v}>{v}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        />
    );

    // Reset version on vendor change
    useEffect(() => {
        if (!vendor) return;

        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // A copy set this vendor together with its version — don't clear the version it just set (that would
        // trigger the auto-select/template path and drop the copied config).
        const copied = copiedConfigRef?.current?.[basePath];
        if (copied && copied.vendor === vendor) return;

        setValue(`${basePath}.version`, '', { shouldDirty: false, shouldValidate: false });
    }, [vendor]);

    // Auto-select the version once a vendor is chosen. Most vendors expose a single version, in which case the
    // dropdown above is hidden — and since the version is required to resolve the vendor's config adapter,
    // leaving it empty makes the save fail with "Version not found". This selects the first (usually only)
    // version whenever the current one isn't valid for the vendor, including right after a vendor switch clears
    // it above.
    useEffect(() => {
        if (!vendor) return;
        const options = byVendor[vendor] ?? [];
        if (options.length === 0) return; // provider list not loaded yet
        if (!version || !options.includes(version)) {
            setValue(`${basePath}.version`, options[0], { shouldDirty: false, shouldValidate: false });
        }
    }, [vendor, version, byVendor, basePath, setValue]);

    // Payment: reconcile the per-integration config list to the vendor's integrations. Keeps entries already in
    // the form (edited or just-loaded), restores saved JSON where available, and fetches an empty template for
    // any integration not yet configured — one entry per integration the vendor exposes, in that order. Runs
    // without marking the form dirty so merely opening a location isn't flagged as an edit.
    const reconciling = useRef(false);
    // The vendor whose per-integration configs currently sit in the form. After a vendor SWITCH the in-form
    // entries are stale and must NOT be reused, even when the new vendor happens to share an integration key
    // (e.g. both FreedomPay and GlobalPayments expose "hosted-payment-page") — reusing them would push one
    // vendor's config JSON to the other and fail on its required fields.
    const integrationsVendorRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (kind !== 1 || !vendor || !version) return;
        const offered = integrations ?? [];
        if (offered.length === 0) return; // not loaded yet, or vendor isn't integration-aware
        if (reconciling.current) return;

        // Only reuse the in-form entries when they belong to THIS vendor; after a switch, start clean.
        const formVendorMatches = integrationsVendorRef.current === vendor;
        const current = formVendorMatches
            ? ((getValues(`${basePath}.integrationConfigurations`) as PaymentIntegrationConfig[] | undefined) ?? [])
            : [];
        const offeredKeys = offered.map(o => o.key);
        // Each integration keeps its OWN version (picked per accordion), so we do NOT clamp them to one value —
        // aligned just means one entry per offered integration, in order (accordions bind by index), each with a
        // version set. New entries default to the vendor's first version.
        const defaultVersion = versions[0] ?? version ?? '';
        const aligned =
            current.length === offeredKeys.length &&
            offeredKeys.every((k, i) => current[i]?.integrationType === k) &&
            current.every(c => !!c.version);
        if (aligned) return;

        // Saved (persisted) entries belong to the last-SAVED vendor. Only restore them when that's the vendor
        // now selected — otherwise a shared integration key would restore the wrong vendor's JSON.
        const savedVendor = (formState.defaultValues as any)?.[basePath]?.vendor;
        const saved = savedVendor === vendor
            ? (((formState.defaultValues as any)?.[basePath]?.integrationConfigurations as PaymentIntegrationConfig[] | undefined) ?? [])
            : [];

        // A just-copied config (Copy Configuration) for THIS vendor takes precedence over saved/template, so the
        // copied per-integration JSON survives the vendor switch the copy triggers (the form's own entries are
        // dropped as stale because they belonged to the previous vendor).
        const copiedCfg = copiedConfigRef?.current?.[basePath];
        const copiedIntegrations = (copiedCfg && copiedCfg.vendor === vendor)
            ? (copiedCfg.integrationConfigurations ?? [])
            : [];

        reconciling.current = true;
        (async () => {
            try {
                const next: PaymentIntegrationConfig[] = [];
                for (const o of offered) {
                    const existing = current.find(c => c.integrationType === o.key);
                    const copiedEntry = copiedIntegrations.find(c => c.integrationType === o.key);
                    const savedEntry = saved.find(c => c.integrationType === o.key);
                    // enabled defaults to true (a not-yet-persisted or older row is on unless explicitly off).
                    if (existing) { next.push({ ...existing, version: existing.version || defaultVersion, enabled: existing.enabled ?? true }); continue; }
                    if (copiedEntry) { next.push({ ...copiedEntry, version: copiedEntry.version || defaultVersion, enabled: copiedEntry.enabled ?? true }); continue; }
                    if (savedEntry) { next.push({ ...savedEntry, version: savedEntry.version || defaultVersion, enabled: savedEntry.enabled ?? true }); continue; }
                    const empty = await providerConfigurationApi.getEmpty(kind, vendor, defaultVersion, o.key);
                    next.push({ integrationType: o.key, version: defaultVersion, configurationJson: empty?.configurationJson ?? '{}', enabled: true });
                }
                setValue(`${basePath}.integrationConfigurations`, next, { shouldDirty: false, shouldValidate: false });
                // These entries now belong to this vendor — future reconciles may reuse them.
                integrationsVendorRef.current = vendor;
            } finally {
                reconciling.current = false;
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind, vendor, version, integrations]);

    // Lock the loyalty vendor to the brand's selection: a location must run the brand's loyalty program, so
    // force the field to the brand vendor (and the Select below is read-only). Waits for the brand to load.
    useEffect(() => {
        if (!loyaltyVendorLocked || !brandLoyaltyVendor) return;
        if (vendor !== brandLoyaltyVendor) {
            setValue(`${basePath}.vendor`, brandLoyaltyVendor, { shouldDirty: false, shouldValidate: false });
        }
    }, [loyaltyVendorLocked, brandLoyaltyVendor, vendor, basePath, setValue]);

    return (
        <Box sx={{ height: '100%' }}>
            <TabContext value={tabValue}>
                <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
                    {(kind === 0 || kind === 1 || kind === 3 || kind === 4 || kind === 7) &&
                        <Box sx={{ height: '100%', borderRight: 1, borderColor: 'divider' }}>
                            <TabList orientation="vertical" onChange={(_event, newValue) => setTabValue(newValue)} aria-label="tabs"
                                sx={{ 
                                        '& .MuiTab-root': {
                                            width: 160,
                                            backgroundColor: (theme) => theme.palette.background.default,
                                        },
                                        '& .MuiTab-root.Mui-selected': {
                                            backgroundColor: (theme) => theme.palette.primaryContainer,
                                            color: (theme) => theme.palette.onPrimaryContainer,
                                            fontWeight: 'bold',
                                        },
                                    }}
                            >
                                <TabWithError label="Vendor" value="vendor" hasError={vendorTabError} />
                                {/* Everything below Vendor belongs to a vendor, and none of it is saved without
                                    one: the location save drops a configuration with no vendor, exactly as the
                                    backend does. Offering the tabs anyway let people fill in General, Tenders
                                    and Field Maps, get a successful save, and find the work gone on reopening
                                    (TTD-167). Nothing is lost by hiding them — there was nowhere to put it. */}
                                {/* Loyalty has no General fields; feedback's General is gated on the brand having feedback enabled. */}
                                {hasVendor && kind !== 3 && !(kind === 4 && !brandFeedbackEnabled) &&
                                    <TabWithError label="General" value="general" hasError={generalTabError} />
                                }
                                {hasVendor && kind === 0 &&
                                    <TabWithError label="Tenders" value="tenders" hasError={tendersTabError} />
                                }
                                {hasVendor && kind === 0 &&
                                    <TabWithError label="Field Maps" value="fieldmaps" hasError={fieldMapsTabError} />
                                }
                                {hasVendor && showWallets &&
                                    <TabWithError label="Wallets" value="wallets" hasError={walletsTabError} />
                                }
                            </TabList>
                        </Box>
                    }
                    <Box>
                        <TabPanel id="vendor" value="vendor" keepMounted>
                            <Stack direction="row" spacing={2}>
                                <Controller
                                    name={`${basePath}.vendor`}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <FormControl sx={{ width: '30ch' }} error={!!fieldState.error}>
                                            <InputLabel id="vendor-label">{strings.vendor}</InputLabel>
                                            <Select
                                                id="vendor"
                                                labelId="vendor-label"
                                                label={strings.vendor}
                                                value={field.value ?? ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                onBlur={field.onBlur}
                                                inputRef={field.ref}
                                                disabled={loyaltyVendorLocked}
                                            >
                                                {(kind === 3 || kind === 4) &&
                                                    <MenuItem value="">
                                                        <em>{strings.none}</em>
                                                    </MenuItem>
                                                }
                                                {(providers ?? []).map((p) => (
                                                    <MenuItem key={p.vendor} value={p.vendor}>
                                                        {p.vendor}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {loyaltyVendorLocked &&
                                                <FormHelperText>{strings.loyaltyVendorBrandManaged}</FormHelperText>
                                            }
                                        </FormControl>
                                    )}
                                />
                                {/* Payment vendors pick a version per integration (in the accordions below, or
                                    auto-selected for a non-integration-aware vendor), so the location-level version
                                    control is not rendered for payment at all. Other kinds show it only when there's
                                    more than one version to choose. Conditional render — not a `hidden` attribute,
                                    which MUI's inline-flex FormControl overrides. */}
                                {kind !== 1 && (versions ?? []).length > 1 && (
                                    <Controller
                                        name={`${basePath}.version`}
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <FormControl sx={{ width: '20ch' }} error={!!fieldState.error}>
                                                <InputLabel id="version-label">{strings.version}</InputLabel>
                                                <Select
                                                    id="version"
                                                    labelId="version-label"
                                                    label={strings.version}
                                                    value={field.value ?? ''}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    onBlur={field.onBlur}
                                                    inputRef={field.ref}
                                                >
                                                    {(versions ?? []).map((v) => (
                                                        <MenuItem key={v} value={v}>{v}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                )}
                            </Stack>
                            <Spacer y={2} />
                            {/* Payment (1), POS (0) and Loyalty (3) vendors have no external/site id at this
                                level — it lives in the provider-specific config JSON. */}
                            {kind !== 1 && kind !== 0 && kind !== 3 && (
                                <FormTextField
                                    id="externalId"
                                    label={strings.externalId}
                                    variant="outlined"
                                    sx={{ width: '30ch' }}
                                    {...register(`${basePath}.externalId`)}
                                    isEditing={isEditing}
                                />
                            )}
                            {/* Google review prompting (feedback only). These are columns on the feedback
                                configuration row, not part of the vendor's config JSON, which is why they sit
                                beside External ID rather than in the editor below. Until now the API carried
                                both but nothing in the portal could set either (TTD-166). */}
                            {kind === 4 && hasVendor && (
                                <>
                                    <Spacer y={2} />
                                    <FormControl size="input">
                                        <FormControlLabel
                                            label={strings.googleReviewEnabled}
                                            control={
                                                <Controller
                                                    name={`${basePath}.googleReviewEnabled`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Switch checked={field.value ?? false} onChange={(e) => field.onChange(e.target.checked)} disabled={!isEditing} />
                                                    )}
                                                />
                                            }
                                        />
                                    </FormControl>
                                    <Spacer y={2} />
                                    <FormTextField
                                        id="googlePlaceId"
                                        label={strings.googlePlaceId}
                                        variant="outlined"
                                        sx={{ width: '30ch' }}
                                        {...register(`${basePath}.googlePlaceId`)}
                                        isEditing={isEditing}
                                    />
                                </>
                            )}
                            {useIntegrationAccordions ? (
                                // One config accordion per integration the vendor exposes (card-not-present +
                                // card-present). Vendor/methods/wallet (the other tabs) are shared across them;
                                // each integration keeps its own config JSON. A "shared" pseudo-integration, if
                                // present, is rendered as the first accordion (expanded) — the creds edited once.
                                <>
                                    {sharedIndex >= 0 && (
                                        <Accordion key={(integrations ?? [])[sharedIndex].key} defaultExpanded disableGutters>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography sx={{ fontWeight: 'bold', alignSelf: 'center' }}>{(integrations ?? [])[sharedIndex].displayName}</Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1, alignSelf: 'center' }}>
                                                    Shared across all integrations
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    {renderIntegrationVersion(sharedIndex)}
                                                    <Controller
                                                        name={`${basePath}.integrationConfigurations.${sharedIndex}.configurationJson`}
                                                        control={control}
                                                        defaultValue="{}"
                                                        render={({ field }) => (
                                                            <JsonEditor
                                                                value={field.value ?? "{}"}
                                                                inputRef={field.ref}
                                                                onChange={field.onChange}
                                                                sx={{ width: "1024px" }}
                                                            />
                                                        )}
                                                    />
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    )}
                                    {(integrations ?? []).map((integ, index) => (
                                        integ.key === SHARED_INTEGRATION_KEY ? null : (
                                            // With a shared accordion above, it's the expanded "first"; otherwise
                                            // fall back to expanding the vendor's first real integration.
                                            <Accordion key={integ.key} defaultExpanded={sharedIndex < 0 && index === 0} disableGutters>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Typography sx={{ fontWeight: 'bold', alignSelf: 'center' }}>{integ.displayName}</Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1, alignSelf: 'center' }}>
                                                        {integ.concept.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                                    </Typography>
                                                    {/* Per-integration enable/disable, only when the vendor exposes more than one.
                                                        A disabled integration is skipped by readiness + client onboarding. The
                                                        control lives in the summary, so stop clicks from toggling the accordion. */}
                                                    {showEnabledToggle && (
                                                        <Controller
                                                            name={`${basePath}.integrationConfigurations.${index}.enabled`}
                                                            control={control}
                                                            defaultValue={true}
                                                            render={({ field }) => (
                                                                <FormControlLabel
                                                                    sx={{ ml: 'auto', mr: 1 }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    control={
                                                                        <Switch
                                                                            checked={field.value ?? true}
                                                                            onChange={(e) => field.onChange(e.target.checked)}
                                                                        />
                                                                    }
                                                                    label="Enabled"
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        {renderIntegrationVersion(index)}
                                                        <Controller
                                                            name={`${basePath}.integrationConfigurations.${index}.configurationJson`}
                                                            control={control}
                                                            defaultValue="{}"
                                                            render={({ field }) => (
                                                                <JsonEditor
                                                                    value={field.value ?? "{}"}
                                                                    inputRef={field.ref}
                                                                    onChange={field.onChange}
                                                                    sx={{ width: "1024px" }}
                                                                />
                                                            )}
                                                        />
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        )
                                    ))}
                                </>
                            ) : (
                                <Controller
                                    name={`${basePath}.configurationJson`}
                                    control={control}
                                    defaultValue="{}"
                                    render={({ field }) => (
                                        <JsonEditor
                                            value={field.value ?? "{}"}
                                            inputRef={field.ref}
                                            onChange={field.onChange}
                                            loading={isTemplateFetching}
                                            sx={{ width: "1024px" }}
                                        />
                                    )}
                                />
                            )}
                        </TabPanel>
                        <TabPanel id="general" value="general" keepMounted>
                            {kind === 0 &&
                                <>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <FormControl size="input">
                                            <FormControlLabel
                                                label={strings.sendServiceChargeToPos}
                                                control={
                                                    <Controller
                                                        name={`${basePath}.sendServiceChargeToPos`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Switch checked={field.value ?? false} onChange={(e) => field.onChange(e.target.checked)} />
                                                        )}
                                                    />
                                                }
                                            />
                                        </FormControl>
                                        <FormTextField
                                            id="serviceChargeId"
                                            label={strings.serviceChargeId}
                                            variant="outlined"
                                            sx={{ width: 300 }}
                                            disabled={!sendServiceChargeToPos}
                                            {...register(`${basePath}.serviceChargeId`, { setValueAs: (v) => v === '' ? null : v })}
                                            isEditing={isEditing}
                                        />
                                    </Stack>

                                    
                                    <Spacer y={1} />
                                    <FormControl size="input">
                                        <FormControlLabel
                                            label={strings.modifierPriceIncludedInItemPrice}
                                            control={
                                                <Controller
                                                    name={`${basePath}.modifierPriceIncludedInItemPrice`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Switch checked={field.value ?? false} onChange={(e) => field.onChange(e.target.checked)} />
                                                    )}
                                                />
                                            }
                                        />
                                    </FormControl>
                                    <Spacer y={2} />
                                    <Controller
                                        name={`${basePath}.autoCloseCheck`}
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl sx={{ width: '20ch' }}>
                                                <InputLabel id="autoCloseCheck-label">{strings.autoCloseCheck}</InputLabel>
                                                <Select<string>
                                                    id="autoCloseCheck"
                                                    labelId="autoCloseCheck-label"
                                                    label={strings.autoCloseCheck}
                                                    // Tri-state: null (Not Set) is distinct from false (No). Map to/from strings
                                                    // since a MUI Select value must be a primitive.
                                                    value={field.value === true ? 'true' : field.value === false ? 'false' : ''}
                                                    onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value === 'true')}
                                                    onBlur={field.onBlur}
                                                    inputRef={field.ref}
                                                >
                                                    <MenuItem value=""><em>{strings.notSet}</em></MenuItem>
                                                    <MenuItem value="true">{strings.yes}</MenuItem>
                                                    <MenuItem value="false">{strings.no}</MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                    <Spacer y={3} />
                                    <FormControlLabel
                                        label={strings.enableCheckRetrievalTimePeriod}
                                        control={
                                            <Switch
                                                checked={checkRetrievalEnabled}
                                                onChange={(e) => {
                                                    const enabled = e.target.checked;
                                                    setCheckRetrievalEnabled(enabled);
                                                    // Disabling clears the value — the field is null whenever the period is off.
                                                    if (!enabled) {
                                                        setValue(`${basePath}.checkRetrievalTimePeriodMinutes`, null, { shouldDirty: true });
                                                    }
                                                }}
                                            />
                                        }
                                    />
                                    {checkRetrievalEnabled &&
                                        <>
                                            <Spacer y={1} />
                                            <Controller
                                                name={`${basePath}.checkRetrievalTimePeriodMinutes`}
                                                control={control}
                                                render={({ field }) => (
                                                    <FormTextField
                                                        id="checkRetrievalTimePeriodMinutes"
                                                        label={strings.checkRetrievalTimePeriodMinutes}
                                                        variant="outlined"
                                                        type="number"
                                                        sx={{ width: 300 }}
                                                        // Blank while enabled still means "no period" → null, not NaN.
                                                        value={field.value ?? ''}
                                                        onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                                                        onBlur={field.onBlur}
                                                        inputRef={field.ref}
                                                        isEditing={isEditing}
                                                    />
                                                )}
                                            />
                                        </>
                                    }
                                    <Spacer y={3} />
                                    <FormTextField
                                        id="ghostItemId"
                                        label={strings.ghostItemId}
                                        variant="outlined"
                                        sx={{ width: 300 }}
                                        {...register(`${basePath}.ghostItemId`, { setValueAs: (v) => v === '' ? null : v })}
                                        isEditing={isEditing}
                                    />
                                </>
                            }
                            {kind === 1 &&
                                <>
                                    <FormControl sx={{ width: 400 }}>
                                        <InputLabel id="payment-methods-accepted-label">{strings.paymentMethodsAccepted}</InputLabel>
                                        <Controller
                                            name={`${basePath}.paymentMethodsAccepted`}
                                            control={control}                                            
                                            render={({ field }) => (
                                                <BitFlagSelect
                                                    labelId="payment-methods-accepted-label"
                                                    id="payment-methods-accepted"
                                                    label={strings.paymentMethodsAccepted}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    options={[
                                                        { value: PaymentMethodsAccepted.Visa, label: strings.visa },
                                                        { value: PaymentMethodsAccepted.Mastercard, label: strings.masterCard },
                                                        { value: PaymentMethodsAccepted.Amex, label: strings.amex },
                                                        { value: PaymentMethodsAccepted.Discover, label: strings.discover },
                                                        { value: PaymentMethodsAccepted.RoomCharge, label: strings.roomCharge },
                                                        { value: PaymentMethodsAccepted.Diners, label: strings.dinersClub },
                                                        { value: PaymentMethodsAccepted.JCB, label: strings.jcb },
                                                        { value: PaymentMethodsAccepted.GiftCard, label: strings.giftCard },
                                                        { value: PaymentMethodsAccepted.Cash, label: strings.cash },
                                                    ]}
                                                />
                                            )}
                                        />
                                    </FormControl>                    
                                    <Spacer y={1} />
                                    <FormControl size="input">
                                        <FormControlLabel
                                            label={strings.allowGiftCardTipping}
                                            control={
                                                <Controller
                                                    name={`${basePath}.allowGiftCardTipping`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Switch checked={field.value ?? false} onChange={(e) => field.onChange(e.target.checked)} />
                                                    )}
                                                />
                                            }
                                        />
                                    </FormControl>
                                </>
                            }
                            {kind === 0 && !!loyaltyVendor &&
                                <>
                                    <Spacer y={2} />
                                    <FormTextField
                                        id="loyaltyItemId"
                                        label={strings.loyaltyItemId}
                                        variant="outlined"
                                        sx={{ width: 400 }}
                                        {...register(`${basePath}.loyaltyItemId`, { setValueAs: (v) => v === '' ? null : v })}
                                        isEditing={isEditing}
                                    />
                                </>
                            }
                            {/* The per-location TouchTab Feedback switch used to live here. TouchTab is a
                                vendor now, so it is chosen from the vendor dropdown above like Akira or
                                Tattle - a location collects feedback one way, not two. */}
                            {kind === 7 &&
                                <>
                                    
                                                                                     
                                </>
                            }
                        </TabPanel>
                        <TabPanel id="tenders" value="tenders" keepMounted>
                            <FormControl size="input">
                                <FormControlLabel
                                    label={strings.useTouchTabTenderType}
                                    control={
                                        <Controller
                                            name={`${tenderPath}.useTouchTabTenderId`}
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value ?? false}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        field.onChange(checked);
                                                        // Clear the group that no longer applies so we don't persist conflicting ids.
                                                        if (checked) {
                                                            cardTenderPaths.forEach(p => setValue(p, undefined, { shouldDirty: true, shouldValidate: false }));
                                                        } else {
                                                            setValue(`${tenderPath}.touchTabTenderId`, undefined, { shouldDirty: true, shouldValidate: false });
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    }
                                />
                            </FormControl>
                            <Spacer y={1} />
                            {useTouchTabTenderType &&
                                <FormTextField
                                    id="touchTabTenderId"
                                    label={strings.touchTabTenderTypeId}
                                    variant="outlined"
                                    sx={{ width: 300 }}
                                    {...register(`${tenderPath}.touchTabTenderId`, {
                                        validate: (v) => !getValues(`${tenderPath}.useTouchTabTenderId`) || !!v || strings.touchTabTenderTypeIdRequired,
                                    })}
                                    error={!!getFieldState(`${tenderPath}.touchTabTenderId`, formState).error}
                                    helperText={getFieldState(`${tenderPath}.touchTabTenderId`, formState).error?.message}
                                    isEditing={isEditing}
                                />
                            }
                            {!useTouchTabTenderType &&
                                <Stack spacing={2} sx={{ maxWidth: 300 }}>
                                    {cardTenderFields.map((f) => {
                                        const reg = register(`${tenderPath}.${f.key}`, { validate: validateAtLeastOneCard });
                                        return (
                                            <FormTextField
                                                key={f.key}
                                                id={f.key}
                                                label={f.label}
                                                variant="outlined"
                                                {...reg}
                                                onChange={(e) => { reg.onChange(e); trigger(cardTenderPaths); }}
                                                error={!!getFieldState(`${tenderPath}.${f.key}`, formState).error}
                                                isEditing={isEditing}
                                            />
                                        );
                                    })}
                                    {getFieldState(`${tenderPath}.visaTenderId`, formState).error &&
                                        <FormHelperText error>{getFieldState(`${tenderPath}.visaTenderId`, formState).error?.message}</FormHelperText>
                                    }
                                </Stack>
                            }
                            <Spacer y={2} />
                            {/* Shown only when Gift Card is an accepted payment method, like cash below.
                                Gift card sits outside the TouchTab-tender toggle above for the same reason:
                                that toggle collapses CARD payments onto a single POS tender, and a gift card
                                is its own tender in either mode. */}
                            {!!giftCardAccepted &&
                                <FormTextField
                                    id="giftCardTenderId"
                                    label={strings.giftCard}
                                    variant="outlined"
                                    sx={{ width: 300 }}
                                    {...register(`${tenderPath}.giftCardTenderId`)}
                                    isEditing={isEditing}
                                />
                            }
                            <Spacer y={2} />
                            {/* Shown only when Cash is an accepted payment method, and required once it is.
                                Cash sits outside the TouchTab-tender toggle above: that toggle collapses CARD
                                payments onto a single POS tender, and cash is its own tender in either mode. */}
                            {!!cashAccepted &&
                                <FormTextField
                                    id="cashTenderId"
                                    label={strings.cash}
                                    variant="outlined"
                                    sx={{ width: 300 }}
                                    {...register(`${tenderPath}.cashTenderId`, {
                                        validate: (v) => !cashAccepted || !!v || strings.cashTenderIdRequired,
                                    })}
                                    error={!!getFieldState(`${tenderPath}.cashTenderId`, formState).error}
                                    helperText={getFieldState(`${tenderPath}.cashTenderId`, formState).error?.message}
                                    isEditing={isEditing}
                                />
                            }
                        </TabPanel>
                        <TabPanel id="fieldmaps" value="fieldmaps" keepMounted>
                            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                                {canUpdateConfig &&
                                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddFieldMap} size="input">
                                    {strings.addFieldMap}
                                </Button>
                                }
                            </Stack>
                            <Box sx={{ height: 360, width: 800 }}>
                                <DataGrid
                                    rows={fieldMapRows}
                                    columns={fieldMapColumns}
                                    autoPageSize
                                    disableRowSelectionOnClick
                                    localeText={{ noRowsLabel: strings.fieldMapsNotFound }}
                                />
                            </Box>

                            <Dialog open={mapDialogOpen} onClose={() => setMapDialogOpen(false)} maxWidth="xs" fullWidth>
                                <DialogTitle>{editingMapIndex === null ? strings.addFieldMap : strings.editFieldMap}</DialogTitle>
                                <DialogContent>
                                    <Stack spacing={2} sx={{ mt: 1 }}>
                                        <FormControl fullWidth>
                                            <InputLabel id="field-map-type-label">{strings.type}</InputLabel>
                                            <Select
                                                labelId="field-map-type-label"
                                                label={strings.type}
                                                value={mapDraft.type}
                                                onChange={(e) => setMapDraft({ ...mapDraft, type: Number(e.target.value) as PosFieldNameMapType })}
                                            >
                                                {Object.entries(PosFieldNameMapType).map(([name, value]) => (
                                                    <MenuItem key={value} value={value}>{prettifyEnumName(name)}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label={strings.externalId}
                                            value={mapDraft.externalId}
                                            onChange={(e) => setMapDraft({ ...mapDraft, externalId: e.target.value })}
                                            fullWidth
                                        />
                                        <TextField
                                            label={strings.value}
                                            value={mapDraft.value}
                                            onChange={(e) => setMapDraft({ ...mapDraft, value: e.target.value })}
                                            fullWidth
                                        />
                                    </Stack>
                                </DialogContent>
                                <DialogActions>
                                    <Button variant="outlined" onClick={() => setMapDialogOpen(false)}>{strings.cancel}</Button>
                                    <Button variant="contained" onClick={saveFieldMap} disabled={!mapDraft.externalId || !mapDraft.value}>
                                        {strings.save}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </TabPanel>
                        {showWallets && <TabPanel id="wallets" value="wallets" keepMounted>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{strings.applePay}</Typography>
                            <FormControl size="input">
                                <FormControlLabel
                                    label={strings.applePayEnabled}
                                    control={
                                        <Controller
                                            name={`${walletPath}.applePayEnabled`}
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value ?? false}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        field.onChange(checked);
                                                        // Clear the Apple Pay fields when disabled so we don't persist stale values.
                                                        if (!checked) {
                                                            setValue(`${walletPath}.applePayMerchantId`, undefined, { shouldDirty: true, shouldValidate: false });
                                                            setValue(`${walletPath}.applePayMerchantCertificate`, undefined, { shouldDirty: true, shouldValidate: false });
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    }
                                />
                            </FormControl>
                            {applePayEnabled &&
                                <Stack spacing={2} sx={{ maxWidth: 400, mt: 1 }}>
                                    <FormTextField
                                        id="applePayMerchantId"
                                        label={strings.applePayMerchantId}
                                        sx={{ width: 400 }}
                                        variant="outlined"
                                        {...register(`${walletPath}.applePayMerchantId`, {
                                            validate: (v) => !getValues(`${walletPath}.applePayEnabled`) || !!v || strings.validationApplePayMerchantIdRequired,
                                        })}
                                        error={!!getFieldState(`${walletPath}.applePayMerchantId`, formState).error}
                                        helperText={getFieldState(`${walletPath}.applePayMerchantId`, formState).error?.message}
                                        isEditing={isEditing}
                                    />
                                    <FormTextField
                                        id="applePayMerchantCertificate"
                                        label={strings.applePayMerchantCertificate}
                                        sx={{ width: 400 }}
                                        variant="outlined"
                                        {...register(`${walletPath}.applePayMerchantCertificate`, {
                                            validate: (v) => !getValues(`${walletPath}.applePayEnabled`) || !!v || strings.validationApplePayMerchantCertificateRequired,
                                        })}
                                        error={!!getFieldState(`${walletPath}.applePayMerchantCertificate`, formState).error}
                                        helperText={getFieldState(`${walletPath}.applePayMerchantCertificate`, formState).error?.message}
                                        isEditing={isEditing}
                                    />
                                </Stack>
                            }
                            <Spacer y={2} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{strings.googlePay}</Typography>
                            <FormControl size="input">
                                <FormControlLabel
                                    label={strings.googlePayEnabled}
                                    control={
                                        <Controller
                                            name={`${walletPath}.googlePayEnabled`}
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value ?? false}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        field.onChange(checked);
                                                        // Clear the Google Pay fields when disabled so we don't persist stale values.
                                                        if (!checked) {
                                                            setValue(`${walletPath}.googlePayMerchantId`, undefined, { shouldDirty: true, shouldValidate: false });
                                                            setValue(`${walletPath}.googlePayMerchantName`, undefined, { shouldDirty: true, shouldValidate: false });
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    }
                                />
                            </FormControl>
                            {googlePayEnabled &&
                                <Stack spacing={2} sx={{ maxWidth: 400, mt: 1 }}>
                                    <FormTextField
                                        id="googlePayMerchantId"
                                        label={strings.googlePayMerchantId}
                                        sx={{ width: 400 }}
                                        variant="outlined"
                                        {...register(`${walletPath}.googlePayMerchantId`, {
                                            validate: (v) => !getValues(`${walletPath}.googlePayEnabled`) || !!v || strings.validationGooglePayMerchantIdRequired,
                                        })}
                                        error={!!getFieldState(`${walletPath}.googlePayMerchantId`, formState).error}
                                        helperText={getFieldState(`${walletPath}.googlePayMerchantId`, formState).error?.message}
                                        isEditing={isEditing}
                                    />
                                    <FormTextField
                                        id="googlePayMerchantName"
                                        label={strings.googlePayMerchantName}
                                        sx={{ width: 400 }}
                                        variant="outlined"
                                        {...register(`${walletPath}.googlePayMerchantName`, {
                                            validate: (v) => !getValues(`${walletPath}.googlePayEnabled`) || !!v || strings.validationGooglePayMerchantNameRequired,
                                        })}
                                        error={!!getFieldState(`${walletPath}.googlePayMerchantName`, formState).error}
                                        helperText={getFieldState(`${walletPath}.googlePayMerchantName`, formState).error?.message}
                                        isEditing={isEditing}
                                    />
                                </Stack>
                            }
                        </TabPanel>}
                    </Box>
                </Box>
            </TabContext>           
        </Box>
    );
};

export default ProviderConfig;
